import { API_BASE_URL } from '../config/serverApiConfig';
import { api } from './api';

export type PeoplePurpose = 'assignee' | 'watcher' | 'group_member' | 'invite';

export interface Person {
  id: string;
  email: string;
  name: string;
  /** How the person is reachable here (a muted reason/role), when the API says. */
  source?: string;
}

export interface PeoplePage {
  people: Person[];
  total: number;
}

export interface PeopleQuery {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface NodePeopleQuery extends PeopleQuery {
  /** User ids the server leaves out before counting and paging. */
  exclude?: string[];
}

const params = (query: PeopleQuery) => {
  const out: Record<string, string | number> = {};
  if (query.search && query.search.trim()) out.search = query.search.trim();
  if (query.limit !== undefined) out.limit = query.limit;
  if (query.offset !== undefined) out.offset = query.offset;
  return out;
};

export const listPeople = async (
  nodeId: number,
  purpose: PeoplePurpose,
  query: NodePeopleQuery = {}
): Promise<PeoplePage> => {
  const response = await api().get(`${API_BASE_URL}r/v2/workspaces/${nodeId}/people`, {
    params: {
      purpose,
      ...params(query),
      ...(query.exclude && query.exclude.length
        ? {
          exclude: query.exclude.join(',')
        }
        : {})
    },
    cache: false
  } as any);
  return response.data;
};

export const listPlatformPeople = async (query: PeopleQuery = {}): Promise<PeoplePage> => {
  const response = await api().get(`${API_BASE_URL}platform/people`, {
    params: { purpose: 'support_admin', ...params(query) },
    cache: false
  } as any);
  return response.data;
};

const RESOLVE_CHUNK = 200; // the API's per-call id limit

/**
 * Names exactly the given user ids (activity, audit actors). Unknown or
 * unreachable ids are absent from the result; callers should treat a rejection
 * as "no names" and fall back to ids or emails.
 */
export const resolvePeople = async (nodeId: number, ids: string[]): Promise<Person[]> => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const out: Person[] = [];
  for (let i = 0; i < unique.length; i += RESOLVE_CHUNK) {
    const response = await api().post(
      `${API_BASE_URL}r/v2/workspaces/${nodeId}/people/resolve`,
      { ids: unique.slice(i, i + RESOLVE_CHUNK) }
    );
    out.push(...(response.data?.people ?? []));
  }
  return out;
};
